import cronstrue from "cronstrue";

const WEEKDAY_MAP: Record<string, number> = {
  sun: 0,
  sunday: 0,
  mon: 1,
  monday: 1,
  tue: 2,
  tuesday: 2,
  wed: 3,
  wednesday: 3,
  thu: 4,
  thur: 4,
  thursday: 4,
  fri: 5,
  friday: 5,
  sat: 6,
  saturday: 6,
};

const MONTH_MAP: Record<string, number> = {
  jan: 1,
  january: 1,
  feb: 2,
  february: 2,
  mar: 3,
  march: 3,
  apr: 4,
  april: 4,
  may: 5,
  jun: 6,
  june: 6,
  jul: 7,
  july: 7,
  aug: 8,
  august: 8,
  sep: 9,
  sept: 9,
  september: 9,
  oct: 10,
  october: 10,
  nov: 11,
  november: 11,
  dec: 12,
  december: 12,
};

export const USAGE_STRING = `Usage:
[at] <time> where time is "midnight" | "noon" | number[:minutes] [am|pm]
every <weekday>[, <weekday> ...] [at <time>] (e.g. "every Monday and Thursday at 10am")
every day [at <time>], daily [at <time>]
every <N> minutes/hours/days
every <N> days at <time>
on the <N>th of every month [at <time>]
every <month> <day> [at <time>]
`;

/**
 * @param {string} text - The natural language schedule description.  See USAGE_STRING.
 * @returns {string|null} - The cron string, or null if parsing fails.
 */
export function naturalLanguageToCron(text: string): string | null {
  const lower = text.toLowerCase().trim();

  // Accept just a number as hour (e.g. '9' means 9am every day)
  const justHour = lower.match(/^(\d{1,2})$/);
  if (justHour) {
    const hour = parseInt(justHour[1], 10);
    if (hour >= 0 && hour <= 23) {
      return `0 ${hour} * * *`;
    }
  }

  // Accept 'every day' as every day at midnight
  if (lower === "every day") {
    return "0 0 * * *";
  }

  // every <days> at <time>
  const multiDayTimeMatch = lower.match(
    /^every ((?:\w+[ ,and]*)+) at ((?:\d{1,2})(?::\d{2})?|midnight|noon)(?: ?(am|pm))?$/
  );
  if (multiDayTimeMatch) {
    const tokens = multiDayTimeMatch[1]
      .replace(/[,]/g, " ")
      .replace(/\band\b/g, " ")
      .split(/\s+/)
      .filter((t) => t.length > 0);
    const days = tokens
      .map((t) => WEEKDAY_MAP[t])
      .filter((d) => d !== undefined)
      .join(",");
    if (days.length > 0) {
      let hour = 0;
      let minute = 0;
      const timePart = multiDayTimeMatch[2];
      const ampm = multiDayTimeMatch[3];
      if ((timePart === "midnight" || timePart === "noon") && ampm) {
        return null;
      }
      if (timePart === "midnight") {
        hour = 0;
        minute = 0;
      } else if (timePart === "noon") {
        hour = 12;
        minute = 0;
      } else {
        // Only allow am/pm with numeric time
        hour = parseInt(timePart.split(":")[0], 10);
        minute = timePart.includes(":") ? parseInt(timePart.split(":")[1], 10) : 0;
        if (ampm) {
          if (ampm === "pm" && hour < 12) hour += 12;
          if (ampm === "am" && hour === 12) hour = 0;
        }
      }
      return `${minute} ${hour} * * ${days}`;
    }
  }

  // next <weekday> at <time>
  const nextDayTimeMatch = lower.match(
    /^next (\w+) at ((?:\d{1,2})(?::\d{2})?|midnight|noon)(?: ?(am|pm))?$/
  );
  if (nextDayTimeMatch) {
    // Cron can't do 'next <weekday>' (one-time), so return null to indicate unsupported
    return null;
  }

  // every <days>
  const multiDayMatch = lower.match(/^every ((?:\w+[, ]*)+)$/);
  if (multiDayMatch) {
    const tokens = multiDayMatch[1]
      .replace(/[,]/g, " ")
      .split(/\s+/)
      .filter((t) => t.length > 0);
    const days = tokens
      .map((t) => WEEKDAY_MAP[t])
      .filter((d) => d !== undefined)
      .join(",");
    if (days.length > 0) {
      return `0 0 * * ${days}`;
    }
  }

  // every day at <time> or daily at <time>
  const dailyTimeMatch = lower.match(
    /^(every day|daily) at ((?:\d{1,2})(?::\d{2})?|midnight|noon)(?: ?(am|pm))?$/
  );
  if (dailyTimeMatch) {
    let hour = 0;
    let minute = 0;
    const timePart = dailyTimeMatch[2];
    const ampm = dailyTimeMatch[3];
    if ((timePart === "midnight" || timePart === "noon") && ampm) {
      return null;
    }
    if (timePart === "midnight") {
      hour = 0;
      minute = 0;
    } else if (timePart === "noon") {
      hour = 12;
      minute = 0;
    } else {
      hour = parseInt(timePart.split(":")[0], 10);
      minute = timePart.includes(":") ? parseInt(timePart.split(":")[1], 10) : 0;
      if (ampm) {
        if (ampm === "pm" && hour < 12) hour += 12;
        if (ampm === "am" && hour === 12) hour = 0;
      }
    }
    return `${minute} ${hour} * * *`;
  }

  // on the 15th of every month at 5:00 PM, at noon, or at midnight
  const monthDayTimeMatch = lower.match(
    /on the (\d{1,2})(st|nd|rd|th) of every month(?: at ((?:\d{1,2})(?::\d{2})?|midnight|noon)(?: ?(am|pm))?)?/
  );
  if (monthDayTimeMatch) {
    const day = monthDayTimeMatch[1];
    let hour = 0;
    let minute = 0;
    if (monthDayTimeMatch[3]) {
      const timePart = monthDayTimeMatch[3];
      const ampm = monthDayTimeMatch[4];
      if ((timePart === "midnight" || timePart === "noon") && ampm) {
        return null;
      }
      if (timePart === "midnight") {
        hour = 0;
        minute = 0;
      } else if (timePart === "noon") {
        hour = 12;
        minute = 0;
      } else {
        hour = parseInt(timePart.split(":")[0], 10);
        minute = timePart.includes(":") ? parseInt(timePart.split(":")[1], 10) : 0;
        if (ampm) {
          if (ampm === "pm" && hour < 12) hour += 12;
          if (ampm === "am" && hour === 12) hour = 0;
        }
      }
    }
    return `${minute} ${hour} ${day} * *`;
  }

  // every January 1st at midnight
  const janFirstMidnight = lower.match(/every january 1st at midnight/);
  if (janFirstMidnight) {
    return "0 0 1 1 *";
  }

  // every <month> <day> at <time> (including noon/midnight)
  const monthDayAtTime = lower.match(
    /^every (\w+) (\d{1,2})(st|nd|rd|th)? at ((?:\d{1,2})(?::\d{2})?|midnight|noon)(?: ?(am|pm))?$/
  );
  if (monthDayAtTime) {
    const monthStr = monthDayAtTime[1];
    const day = parseInt(monthDayAtTime[2], 10);
    let hour = 0;
    let minute = 0;
    const timePart = monthDayAtTime[4];
    const ampm = monthDayAtTime[5];
    if ((timePart === "midnight" || timePart === "noon") && ampm) {
      return null;
    }
    if (timePart === "midnight") {
      hour = 0;
      minute = 0;
    } else if (timePart === "noon") {
      hour = 12;
      minute = 0;
    } else {
      hour = parseInt(timePart.split(":")[0], 10);
      minute = timePart.includes(":") ? parseInt(timePart.split(":")[1], 10) : 0;
      if (ampm) {
        if (ampm === "pm" && hour < 12) hour += 12;
        if (ampm === "am" && hour === 12) hour = 0;
      }
    }
    const monthNum = MONTH_MAP[monthStr];
    if (monthNum && day > 0 && day <= 31) {
      return `${minute} ${hour} ${day} ${monthNum} *`;
    }
  }

  // every <month> <day> at midnight (legacy, for backward compatibility)
  const monthDayMidnight = lower.match(/^every (\w+) (\d{1,2})(st|nd|rd|th)? at midnight$/);
  if (monthDayMidnight) {
    const monthStr = monthDayMidnight[1];
    const day = parseInt(monthDayMidnight[2], 10);
    const monthNum = MONTH_MAP[monthStr];
    if (monthNum && day > 0 && day <= 31) {
      return `0 0 ${day} ${monthNum} *`;
    }
  }

  // every <month> <day> at noon
  const monthDayNoon = lower.match(/^every (\w+) (\d{1,2})(st|nd|rd|th)? at noon$/);
  if (monthDayNoon) {
    const monthStr = monthDayNoon[1];
    const day = parseInt(monthDayNoon[2], 10);
    const monthNum = MONTH_MAP[monthStr];
    if (monthNum && day > 0 && day <= 31) {
      return `0 12 ${day} ${monthNum} *`;
    }
  }

  // every <month> <day>
  const monthDay = lower.match(/^every (\w+) (\d{1,2})(st|nd|rd|th)?$/);
  if (monthDay) {
    const monthStr = monthDay[1];
    const day = parseInt(monthDay[2], 10);
    const monthNum = MONTH_MAP[monthStr];
    if (monthNum && day > 0 && day <= 31) {
      return `0 0 ${day} ${monthNum} *`;
    }
  }

  // at <time> (including noon/midnight)
  const atTimeMatch = lower.match(/^at ((?:\d{1,2})(?::\d{2})?|midnight|noon)(?: ?(am|pm))?/);
  if (atTimeMatch) {
    let hour = 0;
    let minute = 0;
    const timePart = atTimeMatch[1];
    const ampm = atTimeMatch[2];
    if ((timePart === "midnight" || timePart === "noon") && ampm) {
      return null;
    }
    if (timePart === "midnight") {
      hour = 0;
      minute = 0;
    } else if (timePart === "noon") {
      hour = 12;
      minute = 0;
    } else {
      hour = parseInt(timePart.split(":")[0], 10);
      minute = timePart.includes(":") ? parseInt(timePart.split(":")[1], 10) : 0;
      if (ampm) {
        if (ampm === "pm" && hour < 12) hour += 12;
        if (ampm === "am" && hour === 12) hour = 0;
      }
    }
    return `${minute} ${hour} * * *`;
  }

  // every N minutes
  const intervalMinute = lower.match(/every (\d+) minutes?/);
  if (intervalMinute) {
    return `*/${intervalMinute[1]} * * * *`;
  }

  // every N days at <time> (including noon/midnight)
  const intervalDayAtHour = lower.match(
    /every (\d+) days? at ((?:\d{1,2})(?::\d{2})?|midnight|noon)(?: ?(am|pm))?/
  );
  if (intervalDayAtHour) {
    let hour = 0;
    let minute = 0;
    const timePart = intervalDayAtHour[2];
    const ampm = intervalDayAtHour[3];
    if ((timePart === "midnight" || timePart === "noon") && ampm) {
      return null;
    }
    if (timePart === "midnight") {
      hour = 0;
      minute = 0;
    } else if (timePart === "noon") {
      hour = 12;
      minute = 0;
    } else {
      hour = parseInt(timePart.split(":")[0], 10);
      minute = timePart.includes(":") ? parseInt(timePart.split(":")[1], 10) : 0;
      if (ampm) {
        if (ampm === "pm" && hour < 12) hour += 12;
        if (ampm === "am" && hour === 12) hour = 0;
      }
    }
    return `${minute} ${hour} */${intervalDayAtHour[1]} * *`;
  }

  // every N hours
  const intervalHour = lower.match(/every (\d+) hours?/);
  if (intervalHour) {
    return `0 */${intervalHour[1]} * * *`;
  }

  // every N days
  const intervalDay = lower.match(/every (\d+) days?/);
  if (intervalDay) {
    return `0 0 */${intervalDay[1]} * *`;
  }

  // every N weeks
  const intervalWeek = lower.match(/every (\d+) weeks?/);
  if (intervalWeek) {
    // Cron can't do every N weeks, so return null to indicate unsupported
    return null;
  }

  // daily or every day
  if (lower.includes("every day") || lower.includes("daily")) {
    return "0 0 * * *";
  }

  return null;
}

/**
 * Convert a cron string to a natural language description.
 * @param {string} cronString - The cron string.
 * @returns {string|null} - The natural language description, or null if parsing fails.
 */
export function cronToNaturalLanguage(cronString: string): string | null {
  try {
    // Ensure the cron string has all 5 parts (minute, hour, day of month, month, day of week)
    // cronstrue can sometimes handle 6 parts (including seconds) but our NLtoCron produces 5.
    const parts = cronString.split(" ");
    if (parts.length !== 5) {
      // Attempt to fix common issues, like too many wildcards if NL input was vague
      if (parts.length > 5 && parts.slice(5).every((p) => p === "*")) {
        cronString = parts.slice(0, 5).join(" ");
      } else if (parts.length < 5) {
        // Not enough parts, this is likely an invalid cron string for our use case
        console.warn(`Invalid cron string format: ${cronString}`);
        return "Unable to determine schedule from this cron string.";
      }
      // If still not 5 parts after attempted fix, let cronstrue try but it might be vague
    }

    return cronstrue.toString(cronString, {
      verbose: true, // Provides more descriptive text
      use24HourTimeFormat: false, // Optional: use AM/PM
    });
  } catch (e) {
    console.error(`Error parsing cron string: ${cronString}`, e);
    return "Unable to determine schedule from this cron string."; // Or return null / throw
  }
  return null; // Ensure function always returns a value
}
